import type React from 'react';

import { Button, ProgressStepper, Text } from '@change/ds-core-ui';
import { BaseSlider } from '@change/ds-forms';

import { useI18n } from '@change-corgi/core-react/i18n';

import { usePsfSharePageData } from '#src/app/pages/psf/share/pageContext';
import { useCurrentUserId } from '#src/app/shared/hooks/session';

import { MAX_GOAL, MIN_GOAL } from './constants';
import { useSetGoalStep } from './hooks/useSetGoalStep';

type SetGoalStepProps = {
	onNext: () => void;
};

export const SetGoalStep: React.FC<SetGoalStepProps> = ({ onNext }) => {
	const { translate, translatePlural } = useI18n();
	const { data } = usePsfSharePageData();
	const petitionId = data.petition.id;
	const userId = useCurrentUserId();
	const { goalValue, setGoalValue, saveGoal, emoji, message, trackSliderChange, trackSetGoalClick } = useSetGoalStep({
		petitionId,
		userId,
		initialShareGoal: data.initialShareGoal,
	});

	return (
		<div className="flex flex-col gap-7 px-6">
			<div className="flex flex-col items-center gap-[10px]">
				<Text className="text-typography-strong text-center">
					{translate('corgi.psf.share.sharing_goal.set_goal.title')}
				</Text>
				<ProgressStepper currentStepNumber={2} numberOfSteps={3} />
			</div>

			<div className="flex flex-col gap-10">
				<div className="flex flex-col gap-3">
					<Text size="small" variant="heading" as="h1" className="text-typography-strong m-0">
						{translate('corgi.psf.share.sharing_goal.set_goal.heading')}
					</Text>
					<Text variant="body" className="text-typography-strong">
						{translate('corgi.psf.share.sharing_goal.set_goal.body')}
					</Text>
				</div>

				<div className="mt-7 flex flex-col gap-3 px-4">
					<BaseSlider
						ariaLabel={translate('corgi.psf.share.sharing_goal.slider_aria_label')}
						value={goalValue}
						minValue={MIN_GOAL}
						maxValue={MAX_GOAL}
						step={1}
						onChange={setGoalValue}
						onChangeEnd={trackSliderChange}
						showTooltip
						size="thick"
						tooltipTextSize="medium"
						formatValue={(value: number) => {
							const formattedValue = value >= MAX_GOAL ? `${value}+` : value;
							return translatePlural('corgi.psf.share.sharing_goal.slider_label', value, { count: formattedValue });
						}}
						data-qa="sharing-goal-slider"
						tooltipAlignment="follow"
					/>

					<div className="elevation-low bg-fill-inverse-strong rounded-xl px-4 py-3">
						<Text variant="body" size="medium" className="text-typography-weak">
							<span aria-hidden>{emoji}</span> {message}
						</Text>
					</div>
				</div>

				<div className="py-3">
					<Button
						variant="primary"
						className="w-full"
						onClick={() => {
							saveGoal();
							trackSetGoalClick(goalValue);
							onNext();
						}}
						data-qa="set-goal-share-button"
					>
						{translate('corgi.psf.share.sharing_goal.set_goal_button')}
					</Button>
				</div>
			</div>
		</div>
	);
};
